import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Zap, TrendingUp } from 'lucide-react';
import { trackCTAClick } from '../lib/analytics';

/**
 * Quota Display - Shows user's support generation quota
 *
 * Three visual states:
 *   1. Healthy         → simple green/accent bar
 *   2. Low  (≤1 left)  → red bar + inline "Upgrade to Pro" button
 *   3. Exhausted (0)   → prominent upgrade card explaining exactly what
 *                        to do next (main pain point users reported).
 */
export const QuotaDisplay = ({ quota }) => {
  const navigate = useNavigate();
  const quotaPercentage = quota.limit > 0 ? (quota.used / quota.limit) * 100 : 0;
  const isLow = quota.remaining <= 1 && quota.remaining > 0;
  const isExhausted = quota.remaining <= 0 && quota.limit > 0;

  const goUpgrade = (location) => {
    trackCTAClick('Upgrade to Pro', location);
    // Query param is picked up by Account.js to preselect the
    // subscription tab so the user lands directly on plan options.
    navigate('/dashboard/settings?tab=subscription');
  };

  return (
    <div className="bg-bg-light p-4 rounded-lg mb-4" data-testid="quota-display">
      <div className="flex justify-between items-center mb-2">
        <span className="text-text-secondary text-sm">Generation Quota</span>
        <span
          className={`font-semibold ${
            isExhausted ? 'text-red-400' : isLow ? 'text-amber-300' : 'text-accent'
          }`}
        >
          {quota.remaining} / {quota.limit} remaining
        </span>
      </div>
      <div className="w-full bg-bg-dark rounded-full h-2">
        <div
          className={`h-2 rounded-full transition-all ${
            isExhausted ? 'bg-red-500' : isLow ? 'bg-amber-400' : 'bg-accent'
          }`}
          style={{ width: `${Math.min(100, quotaPercentage)}%` }}
        />
      </div>

      {/* LOW: inline warning + upgrade button */}
      {isLow && (
        <div
          className="mt-3 flex items-center justify-between gap-3 px-3 py-2 bg-amber-500/10 border border-amber-500/25 rounded-lg"
          data-testid="quota-low-cta"
        >
          <p className="text-xs sm:text-sm text-amber-200 flex items-center gap-1.5">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
            Only {quota.remaining} left — upgrade for faster processing.
          </p>
          <button
            onClick={() => goUpgrade('quota-low')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary hover:bg-primary/80 text-white rounded-md text-xs font-semibold shadow-sm shadow-primary/20 transition"
            data-testid="quota-low-upgrade-btn"
          >
            <Zap className="w-3.5 h-3.5" />
            Upgrade to Pro
          </button>
        </div>
      )}

      {/* EXHAUSTED: prominent CTA card with clear next step */}
      {isExhausted && (
        <div
          className="mt-3 rounded-xl border-2 border-primary/30 bg-gradient-to-br from-primary/15 via-bg-dark/30 to-accent/10 p-4"
          data-testid="quota-exhausted-cta"
        >
          <div className="flex items-start gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-primary/20 border border-primary/40 flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-4.5 h-4.5 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-text-primary mb-0.5">
                You&apos;ve used all your free generations
              </p>
              <p className="text-xs text-text-secondary leading-relaxed">
                Subscribe to <span className="text-primary font-semibold">Pro</span> for
                priority processing, higher quotas, and manual support fine-tuning.
              </p>
            </div>
          </div>
          <button
            onClick={() => goUpgrade('quota-exhausted')}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-primary hover:bg-primary/80 text-white rounded-lg text-sm font-bold shadow-md shadow-primary/25 hover:scale-[1.01] transition"
            data-testid="quota-exhausted-upgrade-btn"
          >
            <Zap className="w-4 h-4" />
            Subscribe now — get faster processing
          </button>
          <p className="text-[10px] text-text-muted text-center mt-2">
            No commitment · cancel anytime
          </p>
        </div>
      )}
    </div>
  );
};

export default QuotaDisplay;
